
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, Shield, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { createClient } from '@supabase/supabase-js';
import { toast } from 'sonner';
import AthleteDashboard from '@/pages/modules/AthleteDashboard';

// Create an isolated Supabase client for impersonation to prevent affecting parent session
const isolatedSupabase = createClient(
  'https://pvaumvowzncvrdkkvnqq.supabase.co',
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InB2YXVtdm93em5jdnJka2t2bnFxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDU5MzkwNzYsImV4cCI6MjA2MTUxNTA3Nn0.tzzUBAW0sotu9taBdFv62G3RYDAnP7C5CPluRo4Odcs',
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storageKey: 'parent-athlete-view-session', // Isolate from main session
    }
  }
);

const ParentAthleteView: React.FC = () => {
  const { athleteId } = useParams<{ athleteId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [athleteProfile, setAthleteProfile] = useState<any>(null);
  const [debugLogs, setDebugLogs] = useState<string[]>([]);

  const addDebugLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setDebugLogs(prev => [`[${timestamp}] ${message}`, ...prev.slice(0, 9)]);
  };

  useEffect(() => {
    addDebugLog(`🔐 ParentAthleteView loaded - athleteId: ${athleteId}`);
    authenticateAsAthlete();
  }, [athleteId]);

  const authenticateAsAthlete = async () => {
    try {
      if (!athleteId) {
        addDebugLog(`❌ Missing athlete ID`);
        setError('Missing athlete ID');
        setIsProcessing(false);
        return;
      }

      // Get tokens from URL parameters
      const accessToken = searchParams.get('access_token');
      const refreshToken = searchParams.get('refresh_token');

      if (!accessToken || !refreshToken) {
        addDebugLog(`❌ Missing authentication tokens`);
        setError('Missing authentication tokens');
        setIsProcessing(false);
        return;
      }

      addDebugLog(`Setting athlete session for parent view...`);

      // Set the session using isolated Supabase client
      const { error: sessionError } = await isolatedSupabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken
      });

      if (sessionError) {
        addDebugLog(`❌ Session error: ${sessionError.message}`);
        setError('Failed to authenticate as athlete');
        setIsProcessing(false);
        return;
      }

      // Get athlete profile information
      const { data: athlete, error: athleteError } = await isolatedSupabase
        .from('profiles')
        .select('id, name, username, sport:sport_id(name)')
        .eq('id', athleteId)
        .single();

      if (athleteError || !athlete) {
        addDebugLog(`❌ Athlete profile error: ${athleteError?.message || 'Profile not found'}`);
        setError('Athlete profile not found');
        setIsProcessing(false);
        return;
      }

      setAthleteProfile(athlete);
      addDebugLog(`✅ Found athlete profile: ${athlete.name}`);
      
      // Set context for profile data loading
      localStorage.setItem('activeProfileId', athleteId);
      localStorage.setItem('actingAs', 'parent');
      
      addDebugLog(`✅ Successfully authenticated as athlete for parent view`);
      setIsProcessing(false);

    } catch (error: any) {
      addDebugLog(`❌ Authentication error: ${error.message || error}`);
      setError('Failed to authenticate as athlete');
      setIsProcessing(false);
    }
  };

  const handleBackToParentDashboard = async () => {
    // Clean up the isolated session
    await isolatedSupabase.auth.signOut();
    localStorage.removeItem('activeProfileId');
    localStorage.removeItem('actingAs');
    
    addDebugLog(`↩️ Navigating back to parent dashboard`);
    // Navigate back to parent dashboard
    navigate('/parent-dashboard');
  };

  // Cleanup localStorage when component unmounts
  useEffect(() => {
    return () => {
      localStorage.removeItem('activeProfileId');
      localStorage.removeItem('actingAs');
    };
  }, []);

  if (isProcessing) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <Card className="w-full max-w-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-center space-x-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span>Loading athlete dashboard...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <Card className="w-full max-w-lg">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              <span>Access Denied</span>
            </CardTitle>
            <CardDescription>
              You do not have permission to view this athlete's dashboard.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                {error}
              </p>
              <Button 
                variant="outline"
                onClick={handleBackToParentDashboard}
                className="w-full"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Parent Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Debug logs panel
  const DebugLogsPanel = () => (
    <Card className="border-2 border-blue-500 bg-blue-50 mb-4">
      <CardHeader className="py-2">
        <CardTitle className="text-blue-800">🔐 Parent View Debug Logs</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-1 max-h-40 overflow-y-auto">
          {debugLogs.map((log, index) => (
            <div key={index} className="text-sm font-mono text-blue-700 bg-white p-2 rounded">
              {log}
            </div>
          ))}
        </div>
        <Button 
          onClick={() => setDebugLogs([])} 
          variant="outline" 
          size="sm" 
          className="mt-2"
        >
          Clear Logs
        </Button>
      </CardContent>
    </Card>
  );

  // If validation passes, render the full athlete layout with security indicator
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Security indicator banner */}
      <div className="bg-blue-50 border-b border-blue-200 px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-blue-700">
            <Shield className="h-4 w-4" />
            <span>
              Viewing {athleteProfile?.name}'s dashboard as authorized parent
            </span>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleBackToParentDashboard}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Parent Dashboard
          </Button>
        </div>
      </div>
      
      {/* Debug logs panel */}
      {debugLogs.length > 0 && <DebugLogsPanel />}

      {/* Render the full athlete dashboard */}
      <div className="flex-1 overflow-y-auto bg-gray-50 px-4 py-6">
        <AthleteDashboard />
      </div>
    </div>
  );
};

export default ParentAthleteView;
